'use client'

import Image from 'next/image'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useEffect, useState } from 'react'

type StepCardProps = {
  number: string
  title: string
  subtitle: string
  index: number
  isAnimating: boolean
  currentStep: number
}

const StepCard = ({ number, title, subtitle, index, isAnimating, currentStep }: StepCardProps) => {
  // Calculate the gradient color intensity based on whether this card is active
  const isActive = currentStep === index
  const gradientIntensity = isActive ? 100 : Math.max(0, 40 - Math.abs(currentStep - index) * 20)

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{
        opacity: 1,
        y: isActive ? -15 : 0,
        scale: isActive ? 1.05 : 1,
        zIndex: isActive ? 10 : 1,
        transition: {
          duration: 0.5,
          ease: 'easeInOut',
        },
      }}
      whileHover={{
        scale: 1.03,
        y: -8,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        transition: { duration: 0.3 },
      }}
      className={`group relative flex flex-col items-center justify-center overflow-hidden rounded-2xl p-6 text-center transition-all duration-300 ${
        isActive ? 'shadow-xl' : 'shadow-sm hover:shadow-lg'
      }`}
    >
      {/* Background gradient that varies by active state */}
      <motion.div
        animate={{
          opacity: isActive ? 0.9 : 0.4,
          transition: { duration: 0.5 },
        }}
        className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-80"
        style={{
          background: `linear-gradient(135deg, rgba(255,237,213,${gradientIntensity / 100}) 0%, rgba(254,215,170,${gradientIntensity / 120}) 50%, rgba(254,215,170,0) 100%)`,
        }}
      />

      {/* Pulsing border effect */}
      <motion.div
        animate={{
          opacity: isActive ? [0.3, 1, 0.3] : 0.3,
          scale: isActive ? [1, 1.03, 1] : 1,
          transition: {
            opacity: {
              repeat: Infinity,
              duration: 2,
              ease: 'easeInOut',
            },
            scale: {
              repeat: Infinity,
              duration: 2,
              ease: 'easeInOut',
            },
          },
        }}
        className="absolute inset-0 rounded-2xl group-hover:opacity-100"
        style={{
          border: `2px solid rgba(249,115,22,${gradientIntensity / 120})`,
        }}
      />

      <div className="relative">
        {/* Number indicator */}
        <motion.div
          animate={{
            color: `rgba(249,115,22,${(gradientIntensity + 60) / 100})`,
            transition: { duration: 0.5 },
          }}
          className="mb-4 text-5xl font-bold transition-colors duration-300"
        >
          {number}
        </motion.div>

        {/* Divider line with gradient */}
        <motion.div
          animate={{
            width: isActive ? 24 : 12,
            transition: { duration: 0.5 },
          }}
          className="mx-auto my-2 h-px transition-all duration-300 group-hover:w-16"
          style={{
            background: `linear-gradient(to right, transparent, rgba(249,115,22,${gradientIntensity / 100}), transparent)`,
          }}
        />

        {/* Title */}
        <motion.h3
          animate={{
            scale: isActive ? 1.1 : 1,
            transition: { duration: 0.5 },
          }}
          className="relative text-center text-lg font-semibold transition-colors duration-300 dark:group-hover:text-white"
        >
          {title}
        </motion.h3>

        {/* Subtitle */}
        <motion.p
          animate={{
            opacity: isActive ? 1 : 0.8,
            transition: { duration: 0.5 },
          }}
          className="mt-2 text-sm text-gray-600 dark:text-gray-300"
        >
          {subtitle}
        </motion.p>
      </div>

      {/* Glow effect when active */}
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0.2, 0.5, 0.2],
            transition: {
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
          className="absolute inset-0 -z-10 rounded-2xl bg-orange-400/20 blur-xl"
        />
      )}
    </motion.div>
  )
}

export default function StepsSection() {
  const [isAnimating, setIsAnimating] = useState(true)
  const [currentStep, setCurrentStep] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)

  // Create a ref for the section and use the useInView hook
  const { ref: sectionRef, inView: isInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  })

  // Control the sequential animation
  useEffect(() => {
    // Start animation only when in view and not already started
    if (!isInView || !isAnimating) {
      return
    }

    // When first coming into view, reset to the first step
    if (!hasStarted) {
      setCurrentStep(0)
      setHasStarted(true)
    }

    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        // Cycle through 4 steps instead of 5
        return (prev + 1) % 4
      })
    }, 2000) // Change step every 2 seconds

    return () => clearInterval(interval)
  }, [isAnimating, isInView, hasStarted])

  // Reset animation when out of view
  useEffect(() => {
    if (!isInView && hasStarted) {
      setHasStarted(false)
    }
  }, [isInView, hasStarted])

  return (
    <section ref={sectionRef} className="relative py-24 md:px-0">
      {/* Decorative element - removing gradient corners */}
      {/* <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-64 w-64 rounded-full bg-orange-100 opacity-50 blur-3xl dark:bg-orange-900/20"></div>
        <div className="absolute -right-24 -bottom-24 h-64 w-64 rounded-full bg-orange-100 opacity-50 blur-3xl dark:bg-orange-900/20"></div>
      </div> */}

      <div className="mx-auto max-w-7xl">
        <div className="mb-16 grid grid-cols-1 gap-12 text-center md:grid-cols-2 md:text-left">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="mb-2 inline-block text-lg font-medium text-[#ff4500]"
            >
              <span className="inline-block rounded-full bg-orange-100 px-3 py-1 text-[#ff4500] dark:bg-orange-900/30 dark:text-orange-400">
                So funktioniert's
              </span>
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="mb-6 text-center text-3xl font-bold sm:text-4xl md:text-left lg:text-5xl"
            >
              In 4 einfachen Schritten <br /> zur Volks-Energielösung
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-8 text-center text-lg text-gray-600 md:text-left dark:text-gray-300"
            >
              Buchen Sie jetzt einen Termin, um alle Informationen zu unseren kostengünstigen
              Angeboten zu erhalten und schon in Kürze Stromkosten zu sparen:
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <a
                href="/termin"
                className="group inline-flex items-center justify-center rounded-lg bg-[#ff4500] px-6 py-3 font-medium text-white transition-colors duration-200 hover:bg-[#e03e00]"
              >
                Termin buchen
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="ml-2 h-4 w-4 transform transition-transform group-hover:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </motion.div>
          </div>
          <div className="flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="relative h-64 w-full overflow-hidden rounded-2xl shadow-xl sm:h-80 md:h-96"
            >
              <Image
                src="/images/instalation-process.webp"
                alt="Volks-Energielösung planen"
                fill
                className="object-cover transition-transform duration-700 hover:scale-105"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/0 to-transparent"></div>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
          <StepCard
            number="1"
            title="Termin buchen"
            subtitle="Geben Sie uns die wichtigsten Eckdaten an, damit wir uns optimal auf Ihre Beratung vorbereiten können."
            index={0}
            isAnimating={isAnimating}
            currentStep={currentStep}
          />
          <StepCard
            number="2"
            title="Kostenlose Beratung"
            subtitle="Unsere erfahrenen Berater zeigen Ihnen die kostengünstigsten Energielösungen für Ihre Situation."
            index={1}
            isAnimating={isAnimating}
            currentStep={currentStep}
          />
          <StepCard
            number="3"
            title="Volks-Energiekonzept"
            subtitle="Wir erstellen das optimale Energiekonzept für Sie. Mit unseren Lösungen können wir fast jede Immobilie kostengünstig energieeffizient bekommen."
            index={2}
            isAnimating={isAnimating}
            currentStep={currentStep}
          />
          <StepCard
            number="4"
            title="Installation & Inbetriebnahme"
            subtitle="Wir installieren bundesweit mit regionalen Handwerksbetrieben. Mit unserer Kunden-App haben Sie dabei alles im Blick."
            index={3}
            isAnimating={isAnimating}
            currentStep={currentStep}
          />
        </div>
      </div>
    </section>
  )
}
